/* eslint-disable react/jsx-key */
import React from 'react';
import { observer } from 'mobx-react';
import { uniq } from 'lodash';
import dayjs, { Dayjs } from 'dayjs';

import { getErrorMessage, isNullOrEmpty } from 'common-utils';
import { convertDateByTZ, DATE_TIME_FORMAT, formatDate, NO_TIMEZONE } from 'common-utils/date-utils';
import { AccountType, IAccount } from 'dto/access-management/account-dto';
import { IDisplayDeploymentGroup } from 'dto/deployment/deployment-group-dto';
import { IDeploymentTarget } from 'dto/deployment/deployment-dto';
import { MESSAGE_TYPE, NOTIFICATION_TYPE } from 'dto/notifications-dto';
import { DeviceGroupDto } from 'dto/device-group-dto';
import { useDeviceGroupCommonStates } from 'pages/private/devices/device-groups';
import { DeviceGroupMultipleSelectItem } from 'pages/private/components/device-groups-select';
import { showMissingPackageDependenciesModal } from 'pages/private/components/package-modal-components';
import { useAuthStore, useDeploymentStore, useDeviceGroupsStore, useStores } from 'store';
import {
    BasicForm,
    DateTimePickerFormItem,
    HorizontalStepper,
    IStepItem,
    notification,
    showConfirmDialog,
    Spinner,
    SwitchSelectFormItem,
    Typography,
} from 'ui-lib';

import { AddReleaseStep } from './wizard-steps/add-release';
import { CompareReleasesStep } from './wizard-steps/compare-releases';
import { ReviewAndSaveStep } from './wizard-steps/review-and-save';
import deploymentCss from '../../deployments.css';
import { IAssureDeploymentDraft } from '../deployment-create.types';
import { useDeploymentPageNavigation } from '../../hooks/use-deployment-page-navigation';

enum DeploymentTargetType {
    DEVICE_GROUP = 'DEVICE_GROUP',
}

interface IAssureDeploymentCreationWizardProps {
    account: IAccount;
    deploymentGroup: IDisplayDeploymentGroup;
    deploymentName: string;
    draft: IAssureDeploymentDraft;
    onDraftChange: (draft: IAssureDeploymentDraft) => void;
}

const Text = Typography.Text;
const ADD_RELEASE_STEP_CODE = 'add-release';
const COMPARE_RELEASES_STEP_CODE = 'compare-releases';
const ADD_DEPLOYMENT_TARGETS_STEP_CODE = 'add-deployment-targets';
const REVIEW_AND_SAVE_STEP_CODE = 'review-and-save';

export const AssureDeploymentCreationWizard = observer((props: IAssureDeploymentCreationWizardProps): JSX.Element => {
    const { navigateToDeploymentList } = useDeploymentPageNavigation();
    const deploymentStore = useDeploymentStore();
    const deviceGroupStore = useDeviceGroupsStore();
    const { notificationsStore, snapshotStore } = useStores();
    const { currentUser, isDPUAdmin } = useAuthStore();
    const { treeSelectableDataSource, onAccountChanged } = useDeviceGroupCommonStates({
        selectablePlatforms: ['ASSURE'],
    });

    const [errorMessage, setErrorMessage] = React.useState('');
    const [isSaving, setIsSaving] = React.useState(false);

    const updateDraft = (values: Partial<IAssureDeploymentDraft>): void => {
        props.onDraftChange({ ...props.draft, ...values });
    };

    React.useEffect(() => {
        onAccountChanged(props.account.id);
    }, [props.account.id]);

    React.useEffect(() => {
        const selectedDeviceGroups = (props.deploymentGroup.targetDeviceGroupNames || [])
            .map((name) => deviceGroupStore.getDeviceGroupByName(name))
            .filter(Boolean);
        const releases = snapshotStore.getSnapshotsByAccountUuid(props.deploymentGroup.accountUuid);
        const releaseAvailable = !props.draft.releaseId
            || !releases
            || releases.some((release) => release.id === props.draft.releaseId);
        updateDraft({
            selectedDeviceGroups,
            outOfServiceSettings: props.deploymentGroup.outOfServiceSettings,
            releaseId: releaseAvailable ? props.draft.releaseId : undefined,
            removalPackages: releaseAvailable ? props.draft.removalPackages : [],
        });
    }, [props.deploymentGroup.id, snapshotStore.displayPublishedSnapshots]);

    const timezones = React.useMemo<string[]>(() => {
        if (isNullOrEmpty(props.draft.selectedDeviceGroups)) return [];
        return uniq(
            props.draft.selectedDeviceGroups
                .map((group) => group.deviceGroupTimezone || NO_TIMEZONE)
                .filter(Boolean),
        );
    }, [props.draft.selectedDeviceGroups]);

    const dateByTZ = (date: Date, timezone: string): Date =>
        !timezone || timezone === NO_TIMEZONE ? dayjs(date).toDate() : convertDateByTZ(date, timezone);

    const targetDateError = React.useMemo<string[]>(() => {
        if (isNullOrEmpty(props.draft.selectedDeviceGroups)) return ['The Device groups is required.'];
        if (!props.draft.selectedScheduleDate) return ['The Scheduled date is required.'];

        const scheduledDateOver = timezones
            .map((timezone) => {
                const scheduledDate = dateByTZ(props.draft.selectedScheduleDate, timezone);
                return scheduledDate.getTime() < Date.now() ? `The Scheduled date is over in ${timezone}.` : null;
            })
            .filter(Boolean);
        if (!isNullOrEmpty(scheduledDateOver)) return scheduledDateOver;

        if (props.draft.allowDPUOverride) return [];
        if (!props.draft.outOfServiceSettings?.serviceWindowStart || !props.draft.outOfServiceSettings?.serviceWindowEnd) {
            return ['The "Out of service window" is not set so the "DPU override" must be turned ON.'];
        }

        if (props.draft.allowDownloadOnly) {
            if (!props.draft.selectedInstallationDate) return ['The Installation date is required.'];
            if (props.draft.selectedInstallationDate.getTime() < props.draft.selectedScheduleDate.getTime()) {
                return ['The Scheduled date can\'t be greater than the Installation date.'];
            }
        }

        const compareDate = props.draft.allowDownloadOnly
            ? props.draft.selectedInstallationDate
            : props.draft.selectedScheduleDate;
        const start = new Date(
            `${formatDate(compareDate, DATE_TIME_FORMAT.DATE_ONLY)} ${props.draft.outOfServiceSettings.serviceWindowStart}`,
        );
        const end = new Date(
            `${formatDate(compareDate, DATE_TIME_FORMAT.DATE_ONLY)} ${props.draft.outOfServiceSettings.serviceWindowEnd}`,
        );
        const compareStart = compareDate.getTime() - start.getTime();
        const endCompare = end.getTime() - compareDate.getTime();
        const endStart = end.getTime() - start.getTime();
        if ((endStart >= 0 && (compareStart < 0 || endCompare < 0)) || (endStart < 0 && endCompare < 0 && compareStart < 0)) {
            return [
                `The ${props.draft.allowDownloadOnly ? 'Installation' : 'Scheduled'} date must be within the Out of Service Window.`,
            ];
        }
        return [];
    }, [
        props.draft.allowDPUOverride,
        props.draft.allowDownloadOnly,
        props.draft.outOfServiceSettings,
        props.draft.selectedDeviceGroups,
        props.draft.selectedInstallationDate,
        props.draft.selectedScheduleDate,
        timezones,
    ]);

    const onScheduledDateChanged = (value: Dayjs): void => {
        const scheduledDate = value.toDate();
        scheduledDate.setSeconds(0);
        updateDraft({
            selectedScheduleDate: scheduledDate,
            selectedInstallationDate: props.draft.selectedInstallationDate || scheduledDate,
        });
    };

    const onInstallationDateChanged = (value: Dayjs): void => {
        const installationDate = value.toDate();
        installationDate.setSeconds(0);
        updateDraft({ selectedInstallationDate: installationDate });
    };

    const buildDeploymentTargets = (): IDeploymentTarget[] => {
        if (!props.draft.selectedScheduleDate) return [];
        return props.draft.selectedDeviceGroups.map((group) => ({
            targetNames: [group.deviceGroupName],
            deviceGroupIds: [group.id],
            scheduleDateTime: dateByTZ(props.draft.selectedScheduleDate, group.deviceGroupTimezone),
            type: DeploymentTargetType.DEVICE_GROUP,
            dpuOverride: props.draft.allowDPUOverride,
            downloadOnly: props.draft.allowDownloadOnly,
            installDateTime: props.draft.selectedInstallationDate
                ? dateByTZ(props.draft.selectedInstallationDate, group.deviceGroupTimezone)
                : null,
        }));
    };

    const onFinish = async (): Promise<void> => {
        try {
            setIsSaving(true);
            const deployment = await deploymentStore.createDeployment(props.account.id, {
                groupId: props.deploymentGroup.id,
                label: props.deploymentName,
                releaseId: props.draft.releaseId,
                removalPackages: props.draft.removalPackages,
                deploymentTargets: buildDeploymentTargets(),
            });
            const message = `New deployment task ${deployment.label} has been created`;
            notification.success({ message, placement: 'topRight' });
            await notificationsStore.create({
                content: {
                    title: 'Deployments',
                    text: message,
                    messageType: MESSAGE_TYPE.REMOTE_COMMAND,
                },
                type: NOTIFICATION_TYPE.PERSONAL,
                receiverUserUuid: currentUser.uuid,
                sender: currentUser.userName,
            });
            navigateToDeploymentList();
        } catch (error) {
            const message = getErrorMessage(error);
            notification.error({ message, placement: 'topRight' });
            setErrorMessage(message);
        } finally {
            setIsSaving(false);
        }
    };

    const confirmSave = (): void => {
        showConfirmDialog({
            title: 'Schedule Deployment',
            content: 'Are you sure you want to schedule the deployment?',
            okText: 'OK',
            cancelText: 'Cancel',
            onOk: onFinish,
        });
    };

    const targetContent = (
        <div className={deploymentCss.targetSettings}>
            <section className={deploymentCss.settings}>
                <BasicForm
                    cardPros={{ bordered: false }}
                    items={[
                        <DeviceGroupMultipleSelectItem
                            code='deviceGroupsField'
                            label='Device groups'
                            labelAlign='right'
                            isRequired={true}
                            initialValue={props.draft.selectedDeviceGroups.map((group) => group.deviceGroupName)}
                            selectProps={{
                                dataSource: treeSelectableDataSource,
                                expandAll: true,
                                disabled: true,
                                onChange: (values: string[]): void =>
                                    updateDraft({
                                        selectedDeviceGroups: values
                                            .map((name) => deviceGroupStore.getDeviceGroupByName(name))
                                            .filter(Boolean),
                                    }),
                            }}
                        />,
                        isDPUAdmin && (
                            <SwitchSelectFormItem
                                code='dpuOverideField'
                                label='DPU override'
                                labelAlign='right'
                                switchSelectProps={{
                                    defaultChecked: props.draft.allowDPUOverride,
                                    onChange: (value): void =>
                                        updateDraft({ allowDPUOverride: value, allowDownloadOnly: false }),
                                }}
                            />
                        ),
                        !props.draft.allowDPUOverride && (
                            <SwitchSelectFormItem
                                code='downloadOnlyField'
                                label='Download only'
                                labelAlign='right'
                                switchSelectProps={{
                                    defaultChecked: props.draft.allowDownloadOnly,
                                    onChange: (value): void => {
                                        const installationDate = dayjs().add(4, 'minutes').toDate();
                                        installationDate.setSeconds(0);
                                        updateDraft({
                                            allowDownloadOnly: value,
                                            selectedInstallationDate: installationDate,
                                        });
                                    },
                                }}
                            />
                        ),
                        <DateTimePickerFormItem
                            code='scheduledDateField'
                            label='Scheduled date'
                            labelAlign='right'
                            disabledDateType='NO_RESTRICTION'
                            showTime={true}
                            minuteStep={5}
                            showNow={false}
                            allowClear={false}
                            initialValue={props.draft.selectedScheduleDate ? dayjs(props.draft.selectedScheduleDate) : null}
                            dateFormat={DATE_TIME_FORMAT.DATE_AND_SHORT_TIME}
                            onChange={onScheduledDateChanged}
                        />,
                        props.draft.allowDownloadOnly && (
                            <DateTimePickerFormItem
                                code='installDateField'
                                label='Installation date'
                                labelAlign='right'
                                disabledDateType='NO_RESTRICTION'
                                showTime={true}
                                minuteStep={5}
                                showNow={false}
                                allowClear={false}
                                initialValue={
                                    props.draft.selectedInstallationDate ? dayjs(props.draft.selectedInstallationDate) : null
                                }
                                dateFormat={DATE_TIME_FORMAT.DATE_AND_SHORT_TIME}
                                onChange={onInstallationDateChanged}
                            />
                        ),
                    ]}
                />
            </section>
            <section className={deploymentCss.description}>
                <ul>
                    <li>
                        <Text type='warning'>
                            <strong>Note:</strong> the Scheduled/Installation date is based on the timezone of the device groups.
                        </Text>
                    </li>
                    {timezones.length > 0 && <li>Timezone: <Text code>{timezones.join(', ')}</Text></li>}
                    {props.draft.outOfServiceSettings && (
                        <li>
                            Out of Service Window:{' '}
                            <Text code>
                                {props.draft.outOfServiceSettings.serviceWindowStart} ~{' '}
                                {props.draft.outOfServiceSettings.serviceWindowEnd}
                            </Text>
                        </li>
                    )}
                    {targetDateError.map((error, index) => <li key={index}><Text type='danger'>{error}</Text></li>)}
                </ul>
            </section>
        </div>
    );

    const newDeployment = {
        label: props.deploymentName,
        releaseId: props.draft.releaseId,
        removalPackages: props.draft.removalPackages,
        deploymentTargets: buildDeploymentTargets(),
    };

    const steps: IStepItem[] = [
        {
            code: ADD_RELEASE_STEP_CODE,
            label: 'Add release',
            description: 'Add software and configuration updates',
            content: (
                <AddReleaseStep
                    accountUuid={props.deploymentGroup.accountUuid}
                    selectedReleaseId={props.draft.releaseId}
                    addReleaseToDeployment={(releaseId): void => updateDraft({ releaseId })}
                />
            ),
            navigation: {
                next: {
                    hidden: !props.draft.releaseId,
                    confirm: (action): unknown => {
                        const missingDependencies = snapshotStore.unmetDependencies(props.draft.releaseId);
                        if (isNullOrEmpty(missingDependencies)) return action();
                        return showConfirmDialog({
                            title: 'Warning',
                            content: (
                                <Typography.Text>
                                    Selected release has{' '}
                                    <Typography.Link
                                        onClick={(): void => {
                                            showMissingPackageDependenciesModal({ missingPackageDependencies: missingDependencies });
                                        }}
                                    >
                                        unmet dependencies
                                    </Typography.Link>
                                    . Make sure the device has all required packages installed or select another release. Do you want to proceed?
                                </Typography.Text>
                            ),
                            onOk: action,
                        });
                    },
                },
            },
        },
        {
            code: COMPARE_RELEASES_STEP_CODE,
            label: 'Compare releases',
            description: 'Compare releases before deployment',
            content: (
                <CompareReleasesStep
                    releaseId={props.draft.releaseId}
                    selectedDeploymentGroup={props.deploymentGroup}
                    addRemovalPackages={(removalPackages): void => updateDraft({ removalPackages })}
                />
            ),
            navigation: {
                next: {
                    before: (): void => {
                        if (!props.draft.selectedScheduleDate) {
                            const scheduledDate = dayjs().add(2, 'minutes').toDate();
                            scheduledDate.setSeconds(0);
                            updateDraft({ selectedScheduleDate: scheduledDate });
                        }
                    },
                },
            },
        },
        {
            code: ADD_DEPLOYMENT_TARGETS_STEP_CODE,
            label: 'Add deployment targets',
            description: 'Add a schedule and set device groups',
            content: targetContent,
            navigation: {
                next: {
                    hidden: !isNullOrEmpty(targetDateError),
                },
            },
        },
        {
            code: REVIEW_AND_SAVE_STEP_CODE,
            label: 'Review and save',
            description: 'Save the deployment for execution',
            content: (
                <ReviewAndSaveStep
                    newDeployment={newDeployment}
                    errorMessage={errorMessage}
                    allowDPUOverride={props.draft.allowDPUOverride}
                    allowDownloadOnly={props.draft.allowDownloadOnly}
                    selectedDeviceGroups={props.draft.selectedDeviceGroups}
                    selectedInstallationDate={props.draft.selectedInstallationDate}
                    selectedScheduleDate={props.draft.selectedScheduleDate}
                    targetDateError={targetDateError}
                />
            ),
            navigation: {
                back: {
                    before: (): void => setErrorMessage(''),
                },
            },
        },
    ];

    return (
        <div className={deploymentCss['deployment-wizard']}>
            <Spinner spinning={isSaving} className={deploymentCss.spinner}>
                <HorizontalStepper
                    items={steps}
                    currentStepCode={ADD_RELEASE_STEP_CODE}
                    actions={{
                        canGoBack: true,
                        finish: {
                            label: 'SCHEDULE',
                            onClick: confirmSave,
                        },
                    }}
                />
            </Spinner>
        </div>
    );
});
